import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronRight, Check, ArrowRight } from "lucide-react";
import { Icon } from "@/components/site/Icon";
import { GoldButton, GhostButton, SectionHeading } from "@/components/site/ui";
import { useFetch } from "@/lib/useFetch";
import { PackageCards } from "@/components/home/PackageCards";
import { buyService } from "@/lib/cart";
import { useLead } from "@/context/LeadContext";
import { inr } from "@/lib/api";
import { useSeo } from "@/lib/seo";

// Maps marketing landing slugs to an underlying purchasable service slug (reuses existing catalog)
const MAP = {
    "digital-marketing": "social-media", "social-media": "social-media", "performance-marketing": "social-media",
    "seo": "seo", "google-ads": "seo", "meta-ads": "social-media", "content-marketing": "reels",
    "influencer-marketing": "influencer-marketing", "reels": "reels", "branding": "website-design",
    "website": "website-design", "lead-generation": "website-design", "pr-media": "google-business",
};
const TITLES = {
    "digital-marketing": "Digital Marketing", "social-media": "Social Media Marketing", "performance-marketing": "Performance Marketing",
    "seo": "SEO", "google-ads": "Google Ads", "meta-ads": "Meta Ads", "content-marketing": "Content Marketing",
    "influencer-marketing": "Influencer Marketing", "reels": "Reels & Video", "branding": "Branding",
    "website": "Website & Landing Pages", "lead-generation": "Lead Generation", "pr-media": "PR & Media",
};

export default function MarketingService() {
    const { slug } = useParams();
    const nav = useNavigate();
    const { openLead } = useLead();
    const svcSlug = MAP[slug] || "social-media";
    const title = TITLES[slug] || "Marketing";
    const { data: s } = useFetch(`/services/${svcSlug}`);
    useSeo({ title: `${title} Services | SuDigi`, description: `${title} by SuDigi — strategy, execution and reporting to grow your brand.` });

    const go = () => { buyService(svcSlug); nav("/checkout"); };

    return (
        <>
            <section className="bg-gradient-to-b from-teal-950 via-teal-900 to-teal-800 pt-14 pb-14 grain">
                <div className="container-x">
                    <nav className="flex items-center gap-1.5 text-xs text-cream-soft/70 mb-6"><Link to="/" className="hover:text-gold">Home</Link><ChevronRight className="w-3 h-3" /><span className="text-cream">{title}</span></nav>
                    <div className="max-w-2xl">
                        <h1 className="font-serif text-4xl sm:text-5xl text-cream leading-tight">{title}</h1>
                        <p className="mt-4 text-lg text-cream-soft">{s?.tagline || "Build your brand's digital presence with strategy, content and campaigns that convert."}</p>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <GoldButton onClick={go} data-testid="ms-get-started">Get Started {s ? `— ${inr(s.starting_price)}` : ""} <ArrowRight className="w-4 h-4" /></GoldButton>
                            <GhostButton onClick={() => openLead(title)} data-testid="ms-talk-expert">Talk to a Marketing Expert</GhostButton>
                        </div>
                    </div>
                </div>
            </section>
            {s && (
                <section className="bg-teal-900 py-16">
                    <div className="container-x grid md:grid-cols-2 gap-8">
                        <div className="card-teal rounded-2xl p-7">
                            <h3 className="font-serif text-2xl text-cream mb-4">What's Included</h3>
                            <ul className="space-y-3">{s.features.map((f) => <li key={f} className="flex items-start gap-2.5 text-sm text-cream-soft"><Check className="w-5 h-5 text-growth shrink-0" /> {f}</li>)}</ul>
                        </div>
                        <div className="card-teal rounded-2xl p-7 bg-gradient-to-br from-teal-700 to-teal-800 border border-gold/30">
                            <div className="w-14 h-14 rounded-2xl bg-gold/15 border border-gold/30 flex items-center justify-center mb-4"><Icon name={s.icon} className="w-7 h-7 text-gold" /></div>
                            <h3 className="font-serif text-2xl text-cream">Why it matters</h3>
                            <p className="text-cream-soft mt-3">{s.solution || s.description}</p>
                            <GoldButton onClick={go} className="mt-6">Get Started</GoldButton>
                        </div>
                    </div>
                </section>
            )}
            <div className="pt-4"><div className="container-x text-center flex flex-col items-center pt-10"><SectionHeading center eyebrow="Packages" title="Choose a Growth Package" /></div><PackageCards heading={false} /></div>
        </>
    );
}
